import { LitElement } from '@lit'
import { t } from '@nova/utils'

class NovaVariantPicker extends LitElement {
  static properties = {
    productUrl: { type: String, attribute: 'product-url' },
    sectionId: { type: String, attribute: 'section-id' },
    variantData: { type: Object },
    currentVariant: { type: Object, state: true },
  }

  connectedCallback() {
    super.connectedCallback()
    this._initializeElements()
    this._addEventListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._removeEventListeners()
  }

  _initializeElements() {
    this.$optionInputs = this.querySelectorAll('.js-options-input')
    this.$colorLegend = this.querySelector('.js-color-legend')
  }

  _addEventListeners() {
    for (const $input of this.$optionInputs) {
      $input.addEventListener('change', this._handleOptionChange)
    }

    document.addEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _removeEventListeners() {
    for (const $input of this.$optionInputs) {
      $input.removeEventListener('change', this._handleOptionChange)
    }

    document.removeEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _handleVariantPickerUpdate = (event) => {
    this.updateContent(event.detail.html)
  }

  updateURL() {
    const searchParams = new URLSearchParams(document.location.search)
    if (this.currentVariant?.id) {
      searchParams.set('variant', this.currentVariant.id)
    }
    const url =
      searchParams.size > 0
        ? `${this.productUrl}?${searchParams.toString()}`
        : this.productUrl
    window.history.replaceState({}, '', url)
  }

  _handleOptionChange = (event) => {
    const optionType = event.target.name
    const selectedVariantId = Number.parseInt(event.target.dataset.variantId)

    this.currentVariant = structuredClone(
      this.variantData.find((variant) => variant.id === selectedVariantId),
    )

    if (optionType === 'color' && this.$colorLegend) {
      const colorOptionIndex = Number.parseInt(
        event.target.dataset.colorOptionIndex,
      )
      this.$colorLegend.textContent =
        this.currentVariant.options[colorOptionIndex]
    }

    this.updateURL()
    this._fetchProductSection()
  }

  async _fetchProductSection() {
    const response = await fetch(
      `${this.productUrl}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`,
    )
    const responseText = await response.text()
    const sectionHtml = new DOMParser().parseFromString(
      responseText,
      'text/html',
    )

    const $media = sectionHtml.querySelector('nova-product-gallery')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-gallery', {
        detail: { html: $media },
      }),
    )

    const $variantPicker = sectionHtml.querySelector('nova-variant-picker')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-variant-picker', {
        detail: { html: $variantPicker },
      }),
    )

    const $form = sectionHtml.querySelector('.js-product-form')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-form', {
        detail: { html: $form },
      }),
    )

    const $price = sectionHtml.querySelector('nova-price')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-price', {
        detail: { html: $price },
      }),
    )

    const $quantity = sectionHtml.querySelector('nova-quantity-input')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-quantity-input', {
        detail: { html: $quantity },
      }),
    )
  }

  updateContent(content) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }
    this.requestUpdate()

    this._initializeElements()
    this._addEventListeners()
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-variant-picker', NovaVariantPicker)
